import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowLeft } from 'lucide-react';
import { SEOHead } from '../../components/SEOHead';
import { BlogMeta } from '../../components/BlogMeta';
import { trackPageView } from '../../utils/analytics';

export const CoreWebVitalsGuide = () => {
  useEffect(() => {
    trackPageView('/blog/core-web-vitals-guide');
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Core Web Vitals Guide: LCP, INP, and CLS Explained"
        description="Core Web Vitals are Google's page experience signals. This guide explains LCP, INP, and CLS — what each metric measures, the thresholds, and how to improve them."
        canonical="https://webspeedadvisor.com/blog/core-web-vitals-guide"
        robots="index, follow"
      />

      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors text-sm"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>
          <h1 className="text-4xl md:text-5xl font-black font-chivo mb-6">
            Core Web Vitals Guide: LCP, INP, and CLS Explained
          </h1>
          <BlogMeta published="2025-02-20" readMinutes={9} dark />
          <p className="text-xl text-slate-300">
            Google's Core Web Vitals are the three page experience signals that directly influence Search rankings. Here is what each metric measures, the thresholds you need to hit, and how to improve them.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-12">

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            What Are Core Web Vitals?
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              Core Web Vitals are a subset of Google's Web Vitals initiative — a set of real-world metrics designed to measure the quality of a user's experience on a web page. Unlike synthetic scores, Core Web Vitals reflect how actual users perceive loading speed, interactivity, and visual stability.
            </p>
            <p>
              Google incorporated Core Web Vitals into its Search ranking algorithm in 2021 as part of the Page Experience update. Poor Core Web Vitals scores can suppress your rankings, whilst pages that perform well gain a modest but measurable ranking advantage.
            </p>
            <p>
              There are three Core Web Vitals metrics, each targeting a distinct aspect of the user experience: <strong>Largest Contentful Paint (LCP)</strong> for loading performance, <strong>Interaction to Next Paint (INP)</strong> for responsiveness, and <strong>Cumulative Layout Shift (CLS)</strong> for visual stability.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Largest Contentful Paint (LCP)
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              LCP measures <strong>loading performance</strong> — specifically, the time from when the page first starts loading until the largest visible content element (typically a hero image, a large block of text, or a video thumbnail) has fully rendered in the viewport.
            </p>
            <h3 className="text-xl font-bold text-slate-900">Thresholds</h3>
            <ul className="list-none p-0 space-y-2">
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></span>
                <span><strong>Good:</strong> ≤ 2.5 seconds</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-amber-400 flex-shrink-0"></span>
                <span><strong>Needs improvement:</strong> 2.5 – 4 seconds</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></span>
                <span><strong>Poor:</strong> &gt; 4 seconds</span>
              </li>
            </ul>
            <h3 className="text-xl font-bold text-slate-900">Common causes of a poor LCP</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Slow Time to First Byte (TTFB) from the origin server</li>
              <li>Render-blocking CSS or JavaScript that delays rendering</li>
              <li>Large, unoptimised images that take time to download</li>
              <li>No preloading of the LCP element</li>
            </ul>
            <h3 className="text-xl font-bold text-slate-900">How to improve LCP</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Add a <code>rel="preload"</code> hint for your LCP image so the browser discovers it early</li>
              <li>Serve assets from a Content Delivery Network (CDN) close to your users</li>
              <li>Compress and resize images; use modern formats such as WebP or AVIF</li>
              <li>Reduce server response time by enabling caching and optimising back-end queries</li>
            </ul>
          </div>
        </section>

        <section className="bg-white rounded-xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Interaction to Next Paint (INP)
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              INP measures <strong>responsiveness</strong> — the time between a user interaction (a click, tap, or keyboard input) and the moment the browser renders the next visual response to that interaction. INP looks at all interactions throughout the page lifecycle and reports the worst-case latency.
            </p>
            <p>
              INP replaced First Input Delay (FID) as a Core Web Vital in March 2024. Unlike FID, which only measured the delay before the browser began processing the first interaction, INP captures the full time to paint the next frame — giving a far more accurate picture of perceived responsiveness.
            </p>
            <h3 className="text-xl font-bold text-slate-900">Thresholds</h3>
            <ul className="list-none p-0 space-y-2">
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></span>
                <span><strong>Good:</strong> ≤ 200 milliseconds</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-amber-400 flex-shrink-0"></span>
                <span><strong>Needs improvement:</strong> 200 – 500 milliseconds</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></span>
                <span><strong>Poor:</strong> &gt; 500 milliseconds</span>
              </li>
            </ul>
            <h3 className="text-xl font-bold text-slate-900">Common causes of a poor INP</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Long JavaScript tasks blocking the main thread</li>
              <li>Heavy event handlers that perform expensive DOM operations</li>
              <li>Third-party scripts (analytics, chat widgets, ad tags) consuming CPU</li>
              <li>Excessive re-renders in JavaScript frameworks</li>
            </ul>
            <h3 className="text-xl font-bold text-slate-900">How to improve INP</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Break up long tasks using <code>setTimeout</code>, <code>requestAnimationFrame</code>, or the Scheduler API</li>
              <li>Defer non-critical JavaScript until after the page is interactive</li>
              <li>Audit and reduce third-party scripts where possible</li>
              <li>Minimise DOM size to reduce the cost of style recalculation and layout</li>
            </ul>
          </div>
        </section>

        <section className="bg-white rounded-xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Cumulative Layout Shift (CLS)
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              CLS measures <strong>visual stability</strong> — how much the visible content of a page shifts unexpectedly during the loading phase. Every time an element moves without being triggered by a user interaction, it contributes to the CLS score. A high CLS means users may click the wrong element, lose their reading position, or experience a jarring, unpolished interface.
            </p>
            <p>
              CLS is a unitless score calculated from the product of the <em>impact fraction</em> (how much of the viewport is affected) and the <em>distance fraction</em> (how far elements move). Unexpected layout shifts throughout the page session are aggregated into a final score.
            </p>
            <h3 className="text-xl font-bold text-slate-900">Thresholds</h3>
            <ul className="list-none p-0 space-y-2">
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></span>
                <span><strong>Good:</strong> ≤ 0.1</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-amber-400 flex-shrink-0"></span>
                <span><strong>Needs improvement:</strong> 0.1 – 0.25</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0"></span>
                <span><strong>Poor:</strong> &gt; 0.25</span>
              </li>
            </ul>
            <h3 className="text-xl font-bold text-slate-900">Common causes of a poor CLS</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Images and video embeds without explicit <code>width</code> and <code>height</code> attributes</li>
              <li>Dynamically injected banners, cookie notices, or promotional bars</li>
              <li>Web fonts causing a Flash of Unstyled Text (FOUT) that shifts surrounding content</li>
              <li>Animations that alter layout properties such as <code>top</code>, <code>left</code>, or <code>height</code></li>
            </ul>
            <h3 className="text-xl font-bold text-slate-900">How to improve CLS</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Always set <code>width</code> and <code>height</code> on <code>&lt;img&gt;</code> and <code>&lt;video&gt;</code> elements so the browser can reserve the correct space</li>
              <li>Reserve fixed-height containers for ads, embeds, and dynamic content</li>
              <li>Use <code>font-display: swap</code> or <code>font-display: optional</code> to reduce layout shifts caused by late-loading fonts</li>
              <li>Prefer CSS <code>transform</code> for animations — it avoids triggering layout and compositing is GPU-accelerated</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            How Core Web Vitals Are Measured
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              Core Web Vitals data comes from two distinct sources, and the distinction matters when you are diagnosing and reporting on performance.
            </p>
            <p>
              <strong>Lab data</strong> is collected in a controlled environment using tools such as Lighthouse or PageSpeed Insights. The page is loaded under simulated conditions (a fixed network throttle and CPU speed), which makes results reproducible and useful for development and debugging. However, lab data does not reflect the full diversity of your real users' devices and network conditions.
            </p>
            <p>
              <strong>Field data</strong> (also called Real User Monitoring, or RUM) is collected from actual visits to your site using the Chrome User Experience Report (CrUX). Google uses field data for Core Web Vitals reporting, and Core Web Vitals are used by its ranking systems as part of broader page experience evaluation. Field data reflects the 75th percentile (p75) of all user experiences over a rolling 28-day window, so a single slow visit does not skew your score.
            </p>
            <p>
              For a complete picture, use both: lab data to iterate quickly during development, and field data to confirm that real users are experiencing the improvements you have made.
            </p>
          </div>
        </section>

        <section className="bg-slate-100 rounded-xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            How to Check Your Core Web Vitals
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700 space-y-4">
            <p>
              Several tools give you access to Core Web Vitals data, each with different strengths:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>PageSpeed Insights</strong> — Shows both lab (Lighthouse) and field (CrUX) data for any public URL. A good starting point for a quick audit.
              </li>
              <li>
                <strong>Google Search Console</strong> — The Core Web Vitals report groups your pages by status (Good, Needs Improvement, Poor) based on field data. Useful for identifying which page templates are underperforming across your entire site.
              </li>
              <li>
                <strong>Web Speed Advisor</strong> — Runs a combined Lighthouse analysis and surfaces prioritised, contextual recommendations. Instead of presenting a raw list of audit warnings, it explains the likely cause, the expected impact, and the implementation steps — helping your team move from diagnosis to action faster.
              </li>
            </ul>
          </div>
        </section>

        <section className="bg-primary text-white rounded-xl p-8 text-center">
          <h2 className="text-3xl font-bold font-chivo mb-4">
            Check your Core Web Vitals with Web Speed Advisor
          </h2>
          <p className="text-lg mb-6 text-blue-100">
            Get a prioritised analysis of your LCP, INP, and CLS — with clear guidance on what to fix and how to fix it.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-bold px-8 py-4 rounded-full transition-all"
          >
            <Zap size={24} />
            Check your Core Web Vitals →
          </Link>
        </section>

      </div>
    </div>
  );
};

export default CoreWebVitalsGuide;
